import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, 
  FileText, 
  Calendar,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Percent,
  Users,
  Filter,
  ChevronDown,
  ChevronUp,
  Banknote,
  CreditCard,
  Clock,
  Download,
  BarChart3,
  Receipt,
  Wallet,
  AlertCircle
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(value || 0);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  });
};

// Format time
const formatTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('es-ES', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

// Helper to get first day of current month
const getFirstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};

// Helper to get today's date
const getToday = () => {
  return new Date().toISOString().split('T')[0];
};

// Payment method badge
const PaymentMethodBadge = ({ method, split }) => {
  const getBadgeStyle = (m) => {
    switch(m) {
      case 'efectivo': return 'bg-emerald-600/20 text-emerald-400';
      case 'tarjeta': return 'bg-blue-600/20 text-blue-400';
      case 'mixto': return 'bg-purple-600/20 text-purple-400';
      case 'deuda': return 'bg-amber-600/20 text-amber-400';
      default: return 'bg-zinc-600/20 text-zinc-400';
    }
  };
  
  if (method === 'mixto' && split) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <span className={`px-2 py-0.5 rounded text-xs cursor-pointer ${getBadgeStyle(method)}`}>
            mixto ▾
          </span>
        </PopoverTrigger>
        <PopoverContent side="top" className="bg-zinc-900 border-zinc-700 p-3 w-auto">
          <div className="space-y-1.5 text-xs">
            <p className="font-semibold text-white mb-2">Desglose:</p>
            {split.efectivo > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-emerald-400">Efectivo</span>
                <span className="font-mono text-white">{formatCurrency(split.efectivo)}</span>
              </div>
            )}
            {split.tarjeta > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-blue-400">Tarjeta</span>
                <span className="font-mono text-white">{formatCurrency(split.tarjeta)}</span>
              </div>
            )}
            {split.deuda > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-amber-400">Deuda</span>
                <span className="font-mono text-white">{formatCurrency(split.deuda)}</span>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${getBadgeStyle(method)}`}>
      {method}
    </span>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className={`${color} rounded-lg p-4`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-zinc-400 uppercase tracking-wider">{title}</p>
        <p className="text-xl font-bold text-white mt-1">{value}</p>
        {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
      </div>
      <Icon className="text-zinc-500" size={24} />
    </div>
  </div>
);

const AdvancedReports = () => {
  const { token, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('ventas');
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    dateFrom: getFirstDayOfMonth(),
    dateTo: getToday(),
    metodoPago: 'all',
    categoria: 'all',
    tieneDescuento: 'all',
    vendedorId: '',
    usuarioId: '',
    estado: 'all',
    tipo: 'all'
  });
  
  // Report data
  const [reportData, setReportData] = useState(null);
  
  // Load users and categories on mount
  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [usersRes, catsRes] = await Promise.all([
          axios.get(`${API}/reports/users`, { headers: { Authorization: `Bearer ${token}` }}),
          axios.get(`${API}/reports/categories`, { headers: { Authorization: `Bearer ${token}` }})
        ]);
        setUsers(usersRes.data);
        setCategories(catsRes.data);
      } catch (err) {
        console.error('Error loading metadata:', err);
      }
    };
    loadMeta();
  }, [token]);
  
  // Generate report
  const generateReport = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      let params = new URLSearchParams();
      
      params.append('date_from', filters.dateFrom);
      params.append('date_to', filters.dateTo);
      
      switch (reportType) {
        case 'ventas':
          endpoint = '/reports/sales';
          if (filters.metodoPago !== 'all') params.append('metodo_pago', filters.metodoPago);
          if (filters.categoria !== 'all') params.append('categoria', filters.categoria);
          if (filters.tieneDescuento !== 'all') params.append('tiene_descuento', filters.tieneDescuento);
          if (filters.vendedorId) params.append('vendedor_id', filters.vendedorId);
          break;
        case 'cajas':
          endpoint = '/reports/cash-sessions';
          if (filters.usuarioId) params.append('usuario_id', filters.usuarioId);
          if (filters.estado !== 'all') params.append('estado', filters.estado);
          break;
        case 'gastos':
          endpoint = '/reports/expenses';
          if (filters.tipo !== 'all') params.append('tipo', filters.tipo);
          if (filters.usuarioId) params.append('usuario_id', filters.usuarioId);
          break;
        case 'descuentos':
          endpoint = '/reports/discounts';
          if (filters.metodoPago !== 'all') params.append('metodo_pago', filters.metodoPago);
          if (filters.categoria !== 'all') params.append('categoria', filters.categoria);
          if (filters.vendedorId) params.append('vendedor_id', filters.vendedorId);
          break;
        default:
          throw new Error('Unknown report type');
      }
      
      const response = await axios.get(`${API}${endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(response.data);
      toast.success('Reporte generado');
    } catch (err) {
      console.error('Error generating report:', err);
      toast.error('Error al generar el reporte');
    } finally {
      setLoading(false);
    }
  };
  
  // Export to Excel
  const exportToExcel = () => {
    if (!reportData) return;
    
    const wb = XLSX.utils.book_new();
    let filename = `reporte_${reportType}_${filters.dateFrom}_${filters.dateTo}.xlsx`;
    
    const fmtMoney = (v) => typeof v === 'number' ? Number(v.toFixed(2)) : 0;
    
    switch (reportType) {
      case 'ventas': {
        filename = `ventas_${filters.dateFrom}_${filters.dateTo}.xlsx`;
        const m = reportData.metrics || {};
        const ing = m.ingresos_reales || {};
        const meth = m.por_metodo_pago || {};
        const byCat = m.by_category || {};
        
        // ---------- Sheet 1: Resumen ----------
        const filtersRows = [
          ['REPORTE DE VENTAS'],
          ['Periodo', `${filters.dateFrom}  →  ${filters.dateTo}`],
          ['Método de pago', filters.metodoPago === 'all' ? 'Todos' : filters.metodoPago],
          ['Categoría', filters.categoria === 'all' ? 'Todas' : filters.categoria],
          ['Tiene descuento', filters.tieneDescuento === 'all' ? 'Todos' : filters.tieneDescuento],
          ['Vendedor', filters.vendedorId || 'Todos'],
          [],
          ['MÉTRICAS GENERALES'],
          ['Cantidad de ventas', m.cantidad_ventas || 0],
          ['Ventas con descuento', m.ventas_con_descuento || 0],
          ['Ticket promedio (€)', fmtMoney(m.ticket_promedio)],
          ['Total bruto (€)', fmtMoney(m.total_bruto)],
          ['Total descontado (€)', fmtMoney(m.total_descontado)],
          ['Total neto (€)', fmtMoney(m.total_neto)],
          [],
          ['INGRESOS REALES (con cobros de deuda)'],
          ['Total real (€)', fmtMoney(ing.total)],
          ['Efectivo (€)', fmtMoney(ing.efectivo)],
          ['Tarjeta (€)', fmtMoney(ing.tarjeta)],
          ['Cobros deuda efectivo (€)', fmtMoney(ing.cobros_deuda_efectivo)],
          ['Cobros deuda tarjeta (€)', fmtMoney(ing.cobros_deuda_tarjeta)],
          ['Deuda pendiente (€)', fmtMoney(m.deuda_pendiente)],
          [],
          ['VENTAS BRUTAS POR MÉTODO'],
          ['Efectivo (€)', fmtMoney(meth.efectivo)],
          ['Tarjeta (€)', fmtMoney(meth.tarjeta)],
          ['Deuda (€)', fmtMoney(meth.deuda)],
          ['Mixto (€)', fmtMoney(meth.mixto)],
        ];
        const wsSummary = XLSX.utils.aoa_to_sheet(filtersRows);
        wsSummary['!cols'] = [{ wch: 32 }, { wch: 24 }];
        XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumen');
        
        // ---------- Sheet 2: Por Categoría ----------
        const catRows = Object.entries(byCat).map(([cat, val]) => ({
          'Categoría': cat,
          'Ingreso real (€)': fmtMoney(val),
        }));
        if (catRows.length > 0) {
          catRows.push({ 'Categoría': 'TOTAL', 'Ingreso real (€)': fmtMoney(Object.values(byCat).reduce((a, b) => a + (b || 0), 0)) });
          const wsCat = XLSX.utils.json_to_sheet(catRows);
          wsCat['!cols'] = [{ wch: 20 }, { wch: 18 }];
          XLSX.utils.book_append_sheet(wb, wsCat, 'Por Categoría');
        }
        
        // ---------- Sheet 3: Detalle de Ventas ----------
        const salesRows = (reportData.sales || []).map(s => ({
          'Fecha': formatDate(s.created_at),
          'Hora': formatTime(s.created_at),
          'Cliente': s.customer_name || '-',
          'Teléfono': s.customer_phone || '-',
          'Vendedor': s.seller_name,
          'Productos': (s.items || []).map(i => `${i.item_name} x${i.quantity}`).join(' | '),
          'Subtotal (€)': fmtMoney(s.subtotal),
          'Descuento %': s.descuento_porcentaje || s.discount_percent || 0,
          'Descuento (€)': fmtMoney(s.descuento_monto || s.discount_amount || 0),
          'Total (€)': fmtMoney(s.total_final ?? s.total),
          'Método': s.payment_method,
          'Devuelta': s.refunded ? 'Sí' : 'No',
          'Notas': s.notes || '',
        }));
        const wsSales = XLSX.utils.json_to_sheet(salesRows);
        wsSales['!cols'] = [
          { wch: 12 }, { wch: 8 }, { wch: 22 }, { wch: 14 }, { wch: 16 }, { wch: 40 },
          { wch: 12 }, { wch: 11 }, { wch: 13 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 24 }
        ];
        XLSX.utils.book_append_sheet(wb, wsSales, 'Detalle');
        break;
      }
      case 'cajas': {
        filename = `cajas_${filters.dateFrom}_${filters.dateTo}.xlsx`;
        const data = (reportData.sessions || []).map(s => ({
          'Fecha Apertura': formatDate(s.opened_at),
          'Usuario': s.user_name,
          'Estado': s.status,
          'Efectivo Inicial (€)': fmtMoney(s.initial_cash),
          'Efectivo Final (€)': s.final_cash != null ? fmtMoney(s.final_cash) : '-',
          'Diferencia (€)': fmtMoney(s.difference || 0),
          'Ventas Totales (€)': fmtMoney(s.total_sales || 0),
          'Devoluciones (€)': fmtMoney(s.refunds_total || 0),
          'Neto (€)': fmtMoney(s.net_sales || 0),
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 14 }, { wch: 18 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Cajas');
        break;
      }
      case 'gastos': {
        filename = `gastos_${filters.dateFrom}_${filters.dateTo}.xlsx`;
        const data = (reportData.expenses || []).map(e => ({
          'Fecha': formatDate(e.created_at),
          'Hora': formatTime(e.created_at),
          'Tipo': e.type,
          'Categoría': e.category || '-',
          'Descripción': e.description,
          'Monto (€)': fmtMoney(e.amount),
          'Usuario': e.user_name || '-',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 16 }, { wch: 32 }, { wch: 12 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Gastos');
        break;
      }
      case 'descuentos': {
        filename = `descuentos_${filters.dateFrom}_${filters.dateTo}.xlsx`;
        const data = (reportData.sales || []).map(s => ({
          'Fecha': formatDate(s.created_at),
          'Cliente': s.customer_name || '-',
          'Vendedor': s.seller_name,
          'Subtotal (€)': fmtMoney(s.subtotal),
          'Descuento %': s.descuento_porcentaje || s.discount_percent || 0,
          'Descuento (€)': fmtMoney(s.descuento_monto || s.discount_amount || 0),
          'Total (€)': fmtMoney(s.total_final ?? s.total),
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 12 }, { wch: 22 }, { wch: 16 }, { wch: 13 }, { wch: 11 }, { wch: 13 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Descuentos');
        break;
      }
      default:
        return;
    }
    
    XLSX.writeFile(wb, filename);
    toast.success('Excel exportado');
  };
  
  // Export to PDF
  const exportToPDF = () => {
    if (!reportData) return;
    
    const doc = new jsPDF();
    const title = {
      ventas: 'Reporte de Ventas',
      cajas: 'Reporte de Cierres de Caja',
      gastos: 'Reporte de Gastos y Retiros',
      descuentos: 'Reporte de Descuentos'
    }[reportType];
    
    doc.setFontSize(16);
    doc.text(title, 14, 20);
    doc.setFontSize(10);
    doc.text(`Período: ${filters.dateFrom} al ${filters.dateTo}`, 14, 28);
    
    let columns = [];
    let rows = [];
    
    switch (reportType) {
      case 'ventas':
        columns = ['Fecha', 'Cliente', 'Subtotal', 'Desc.', 'Total', 'Método'];
        rows = reportData.sales.map(s => [
          formatDate(s.created_at),
          (s.customer_name || '-').substring(0, 15),
          formatCurrency(s.subtotal),
          formatCurrency(s.descuento_monto || s.discount_amount || 0),
          formatCurrency(s.total_final || s.total),
          s.payment_method
        ]);
        break;
      case 'gastos':
        columns = ['Fecha', 'Tipo', 'Descripción', 'Monto'];
        rows = reportData.expenses.map(e => [
          formatDate(e.created_at),
          e.type,
          (e.description || '-').substring(0, 25),
          formatCurrency(e.amount)
        ]);
        break;
      case 'descuentos':
        columns = ['Fecha', 'Cliente', 'Subtotal', 'Desc. %', 'Desc. €', 'Total'];
        rows = reportData.sales.map(s => [
          formatDate(s.created_at),
          (s.customer_name || '-').substring(0, 15),
          formatCurrency(s.subtotal),
          `${s.descuento_porcentaje || s.discount_percent || 0}%`,
          formatCurrency(s.descuento_monto || s.discount_amount || 0),
          formatCurrency(s.total_final || s.total)
        ]);
        break;
      default:
        return;
    }
    
    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 35,
      theme: 'grid',
      styles: { fontSize: 8 }
    });
    
    doc.save(`reporte_${reportType}_${filters.dateFrom}.pdf`);
    toast.success('PDF exportado');
  };
  
  // Render sales metrics
  const renderSalesMetrics = () => {
    if (!reportData?.metrics) return null;
    const m = reportData.metrics;
    
    return (
      <>
        {/* Main metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3 mb-4">
          <MetricCard 
            title="Total Bruto" 
            value={formatCurrency(m.total_bruto)} 
            icon={TrendingUp} 
            color="bg-zinc-800/50"
            subtitle="Antes de descuentos"
          />
          <MetricCard 
            title="Descuentos" 
            value={formatCurrency(m.total_descontado)} 
            icon={Percent} 
            color="bg-amber-600/10"
            subtitle={`${m.ventas_con_descuento} ventas`}
          />
          <MetricCard 
            title="Total Facturado" 
            value={formatCurrency(m.total_neto)} 
            icon={Receipt} 
            color="bg-emerald-600/10"
            subtitle="Bruto - Descuentos"
          />
          <MetricCard 
            title="Ventas" 
            value={m.cantidad_ventas} 
            icon={ShoppingCart} 
            color="bg-blue-600/10"
            subtitle={`Ticket: ${formatCurrency(m.ticket_promedio)}`}
          />
        </div>
        
        {/* Real Income section - SIMPLIFIED */}
        {m.ingresos_reales && (
          <div className="bg-zinc-900/50 rounded-lg p-4 mb-4 border border-zinc-800">
            <h4 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
              <TrendingUp size={16} />
              Ingresos Reales (Dinero Cobrado)
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard 
                title="Total Cobrado" 
                value={formatCurrency(m.ingresos_reales.total)} 
                icon={Receipt} 
                color="bg-emerald-600/20"
              />
              <MetricCard 
                title="Efectivo" 
                value={formatCurrency(m.ingresos_reales.efectivo)} 
                icon={Banknote} 
                color="bg-emerald-600/10"
              />
              <MetricCard 
                title="Tarjeta" 
                value={formatCurrency(m.ingresos_reales.tarjeta)} 
                icon={CreditCard} 
                color="bg-blue-600/10"
              />
              <MetricCard 
                title="Deuda Pendiente" 
                value={formatCurrency(m.deuda_pendiente)} 
                icon={AlertCircle} 
                color="bg-red-600/10"
                subtitle="No cobrada aún"
              />
            </div>
          </div>
        )}
      </>
    );
  };
  
  // Render expenses metrics
  const renderExpensesMetrics = () => {
    if (!reportData?.metrics) return null;
    const m = reportData.metrics;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MetricCard 
          title="Total Gastos" 
          value={formatCurrency(m.total_gastos)} 
          icon={TrendingDown} 
          color="bg-red-600/10"
          subtitle={`${m.cantidad_gastos} operaciones`}
        />
        <MetricCard 
          title="Total Retiros" 
          value={formatCurrency(m.total_retiros)} 
          icon={Wallet} 
          color="bg-amber-600/10"
          subtitle={`${m.cantidad_retiros} operaciones`}
        />
        <MetricCard 
          title="Total" 
          value={formatCurrency(m.total)} 
          icon={Receipt} 
          color="bg-zinc-800/50"
        />
      </div>
    );
  };
  
  // Render discounts metrics
  const renderDiscountsMetrics = () => {
    if (!reportData?.metrics) return null;
    const m = reportData.metrics;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <MetricCard 
          title="Total Bruto" 
          value={formatCurrency(m.total_bruto)} 
          icon={TrendingUp} 
          color="bg-zinc-800/50"
        />
        <MetricCard 
          title="Total Descontado" 
          value={formatCurrency(m.total_descontado)} 
          icon={Percent} 
          color="bg-amber-600/10"
        />
        <MetricCard 
          title="Total Neto" 
          value={formatCurrency(m.total_neto)} 
          icon={Receipt} 
          color="bg-emerald-600/10"
        />
        <MetricCard 
          title="Ventas c/Descuento" 
          value={m.cantidad_ventas_con_descuento} 
          icon={ShoppingCart} 
          color="bg-blue-600/10"
        />
        <MetricCard 
          title="Desc. Promedio" 
          value={`${m.porcentaje_descuento_promedio}%`} 
          icon={BarChart3} 
          color="bg-purple-600/10"
          subtitle={formatCurrency(m.descuento_promedio)}
        />
      </div>
    );
  };
  
  // Render sales table
  const renderSalesTable = () => {
    if (!reportData?.sales?.length) {
      return <p className="text-zinc-500 text-center py-8">No hay ventas en el período seleccionado</p>;
    }
    
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-800/50">
              <TableHead className="text-zinc-400">Fecha</TableHead>
              <TableHead className="text-zinc-400">Hora</TableHead>
              <TableHead className="text-zinc-400">Cliente</TableHead>
              <TableHead className="text-zinc-400">Vendedor</TableHead>
              <TableHead className="text-zinc-400">Productos</TableHead>
              <TableHead className="text-zinc-400">Pago</TableHead>
              <TableHead className="text-zinc-400 text-right">Subtotal</TableHead>
              <TableHead className="text-zinc-400 text-right">Desc.</TableHead>
              <TableHead className="text-zinc-400 text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reportData.sales.map((sale, idx) => (
              <TableRow key={sale.id || idx} className="border-zinc-800">
                <TableCell className="text-zinc-300">{formatDate(sale.created_at)}</TableCell>
                <TableCell className="text-zinc-400">{formatTime(sale.created_at)}</TableCell>
                <TableCell className="text-white">{sale.customer_name || '-'}</TableCell>
                <TableCell className="text-zinc-400">{sale.seller_name}</TableCell>
                <TableCell className="text-zinc-300 text-xs max-w-[260px]">
                  <div className="flex flex-col gap-0.5">
                    {(sale.items || []).map((it, i) => (
                      <span key={i} className="truncate">
                        <span className="text-zinc-500">{it.quantity}×</span> {it.item_name}
                      </span>
                    ))}
                    {(!sale.items || sale.items.length === 0) && <span className="text-zinc-600">-</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <PaymentMethodBadge method={sale.payment_method} split={sale.payment_split} />
                </TableCell>
                <TableCell className="text-right font-mono text-zinc-300">
                  {formatCurrency(sale.subtotal)}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {(sale.descuento_monto || sale.discount_amount || 0) > 0 ? (
                    <span className="text-amber-400">
                      -{formatCurrency(sale.descuento_monto || sale.discount_amount)}
                      <span className="text-amber-300 ml-1 text-xs">
                        ({sale.descuento_porcentaje || sale.discount_percent || 0}%)
                      </span>
                    </span>
                  ) : (
                    <span className="text-zinc-500">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-emerald-400">
                  {formatCurrency(sale.total_final || sale.total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };
  
  // Render expenses table
  const renderExpensesTable = () => {
    if (!reportData?.expenses?.length) {
      return <p className="text-zinc-500 text-center py-8">No hay gastos/retiros en el período seleccionado</p>;
    }
    
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-800/50">
              <TableHead className="text-zinc-400">Fecha</TableHead>
              <TableHead className="text-zinc-400">Hora</TableHead>
              <TableHead className="text-zinc-400">Tipo</TableHead>
              <TableHead className="text-zinc-400">Descripción</TableHead>
              <TableHead className="text-zinc-400">Usuario</TableHead>
              <TableHead className="text-zinc-400 text-right">Monto</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reportData.expenses.map((exp, idx) => (
              <TableRow key={exp.id || idx} className="border-zinc-800">
                <TableCell className="text-zinc-300">{formatDate(exp.created_at)}</TableCell>
                <TableCell className="text-zinc-400">{formatTime(exp.created_at)}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    exp.type === 'gasto' ? 'bg-red-600/20 text-red-400' : 'bg-amber-600/20 text-amber-400'
                  }`}>
                    {exp.type}
                  </span>
                </TableCell>
                <TableCell className="text-white">{exp.description || '-'}</TableCell>
                <TableCell className="text-zinc-400">{exp.user_name || '-'}</TableCell>
                <TableCell className="text-right font-mono text-red-400">
                  -{formatCurrency(exp.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };
  
  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-white">Reportes</h1>
        
        {/* Report Type Selector */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'ventas', label: 'Ventas', icon: ShoppingCart },
            { id: 'cajas', label: 'Cierres', icon: Wallet },
            { id: 'gastos', label: 'Gastos', icon: TrendingDown },
            { id: 'descuentos', label: 'Descuentos', icon: Percent }
          ].map(type => (
            <Button
              key={type.id}
              variant={reportType === type.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setReportType(type.id); setReportData(null); }}
              className={reportType === type.id ? 'bg-amber-600 hover:bg-amber-700' : 'border-zinc-700'}
            >
              <type.icon size={16} className="mr-2" />
              {type.label}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Filters Panel */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader 
          className="cursor-pointer py-3"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <CardTitle className="text-white flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Filter size={18} />
              Filtros
            </span>
            {filtersExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </CardTitle>
        </CardHeader>
        
        {filtersExpanded && (
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Date Range - Always visible */}
              <div>
                <Label className="text-zinc-400">Fecha Desde *</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Fecha Hasta *</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              {/* Filters for VENTAS and DESCUENTOS */}
              {(reportType === 'ventas' || reportType === 'descuentos') && (
                <>
                  <div>
                    <Label className="text-zinc-400">Método de Pago</Label>
                    <Select value={filters.metodoPago} onValueChange={(v) => setFilters({...filters, metodoPago: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="mixto">Mixto</SelectItem>
                        <SelectItem value="deuda">Deuda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400">Categoría</Label>
                    <Select value={filters.categoria} onValueChange={(v) => setFilters({...filters, categoria: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="all">Todas</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400">Vendedor</Label>
                    <Select value={filters.vendedorId || 'all'} onValueChange={(v) => setFilters({...filters, vendedorId: v === 'all' ? '' : v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="all">Todos</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {/* Descuento filter - Only for VENTAS */}
              {reportType === 'ventas' && (
                <div>
                  <Label className="text-zinc-400">Con Descuento</Label>
                  <Select value={filters.tieneDescuento} onValueChange={(v) => setFilters({...filters, tieneDescuento: v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Solo con descuento</SelectItem>
                      <SelectItem value="no">Solo sin descuento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Filters for CAJAS */}
              {reportType === 'cajas' && (
                <>
                  <div>
                    <Label className="text-zinc-400">Usuario</Label>
                    <Select value={filters.usuarioId || 'all'} onValueChange={(v) => setFilters({...filters, usuarioId: v === 'all' ? '' : v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="all">Todos</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Estado</Label>
                    <Select value={filters.estado} onValueChange={(v) => setFilters({...filters, estado: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="open">Abiertas</SelectItem>
                        <SelectItem value="closed">Cerradas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {/* Filters for GASTOS */}
              {reportType === 'gastos' && (
                <>
                  <div>
                    <Label className="text-zinc-400">Tipo</Label>
                    <Select value={filters.tipo} onValueChange={(v) => setFilters({...filters, tipo: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="gasto">Gastos</SelectItem>
                        <SelectItem value="retiro">Retiros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Usuario</Label>
                    <Select value={filters.usuarioId || 'all'} onValueChange={(v) => setFilters({...filters, usuarioId: v === 'all' ? '' : v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="all">Todos</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
            
            {/* Generate Button */}
            <div className="flex justify-end mt-4 gap-2">
              <Button 
                onClick={generateReport}
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {loading ? (
                  <>Generando...</>
                ) : (
                  <>
                    <BarChart3 size={16} className="mr-2" />
                    Generar Reporte
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
      
      {/* Results */}
      {reportData && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-white text-base">
              Resultados ({filters.dateFrom} al {filters.dateTo})
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportToExcel} className="border-zinc-700" data-testid="export-excel-btn">
                <FileSpreadsheet size={16} className="mr-2" />
                Excel
              </Button>
              <Button variant="outline" size="sm" onClick={exportToPDF} className="border-zinc-700">
                <FileText size={16} className="mr-2" />
                PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Metrics */}
            {reportType === 'ventas' && renderSalesMetrics()}
            {reportType === 'gastos' && renderExpensesMetrics()}
            {reportType === 'descuentos' && renderDiscountsMetrics()}
            
            {/* Table */}
            {(reportType === 'ventas' || reportType === 'descuentos') && renderSalesTable()}
            {reportType === 'gastos' && renderExpensesTable()}
            {reportType === 'cajas' && (
              <p className="text-zinc-500 text-center py-4">
                {reportData.sessions?.length || 0} sesiones encontradas
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdvancedReports;
